import React, { useState } from "react";
import { Button, Input, Space, Upload, message, Image, Typography } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  YoutubeOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import type { BlogMediaItem } from "../../types/blogPost";
import {
  getBlogMediaPublicUrl,
  parseVideoEmbed,
  removeBlogMediaObject,
  uploadBlogMediaImage,
} from "../../utils/blogMediaUtils";

const { Text } = Typography;

export interface BlogMediaEditorProps {
  postId: string;
  coverImagePath: string | null;
  media: BlogMediaItem[];
  onCoverChange: (path: string | null) => void;
  onMediaChange: (media: BlogMediaItem[]) => void;
}

export function BlogMediaEditor({
  postId,
  coverImagePath,
  media,
  onCoverChange,
  onMediaChange,
}: BlogMediaEditorProps) {
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");

  const coverUrl = getBlogMediaPublicUrl(coverImagePath);

  const handleCoverUpload = async (file: File) => {
    setUploadingCover(true);
    try {
      const result = await uploadBlogMediaImage(postId, file);
      if ("error" in result) {
        message.error(result.error);
        return;
      }
      if (coverImagePath) {
        await removeBlogMediaObject(coverImagePath);
      }
      onCoverChange(result.path);
      message.success("Cover image uploaded");
    } finally {
      setUploadingCover(false);
    }
  };

  const clearCover = async () => {
    if (coverImagePath) {
      await removeBlogMediaObject(coverImagePath);
    }
    onCoverChange(null);
  };

  const handleGalleryImageUpload = async (file: File) => {
    setUploadingGallery(true);
    try {
      const result = await uploadBlogMediaImage(postId, file);
      if ("error" in result) {
        message.error(result.error);
        return;
      }
      onMediaChange([
        ...media,
        { id: crypto.randomUUID(), type: "image", path: result.path },
      ]);
      message.success("Photo added");
    } finally {
      setUploadingGallery(false);
    }
  };

  const addVideo = () => {
    const embed = parseVideoEmbed(videoUrl);
    if (!embed) {
      message.error("Enter a valid YouTube or Vimeo URL");
      return;
    }
    onMediaChange([
      ...media,
      { id: crypto.randomUUID(), type: "video", url: videoUrl.trim() },
    ]);
    setVideoUrl("");
  };

  const removeItem = async (index: number) => {
    const item = media[index];
    if (item?.type === "image") {
      await removeBlogMediaObject(item.path);
    }
    onMediaChange(media.filter((_, i) => i !== index));
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= media.length) return;
    const copy = [...media];
    const tmp = copy[index];
    copy[index] = copy[next];
    copy[next] = tmp;
    onMediaChange(copy);
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Cover image
        </Text>
        {coverUrl ? (
          <Space align="start" wrap>
            <Image
              src={coverUrl}
              alt="Cover"
              width={160}
              style={{ objectFit: "cover", borderRadius: 4 }}
            />
            <Button danger size="small" icon={<DeleteOutlined />} onClick={clearCover}>
              Remove cover
            </Button>
          </Space>
        ) : (
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              void handleCoverUpload(file);
              return false;
            }}
            disabled={uploadingCover}
          >
            <Button icon={<PictureOutlined />} loading={uploadingCover}>
              Upload cover
            </Button>
          </Upload>
        )}
      </div>

      <div>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Gallery
        </Text>
        <Space wrap style={{ marginBottom: 12 }}>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              void handleGalleryImageUpload(file);
              return false;
            }}
            disabled={uploadingGallery}
          >
            <Button icon={<PlusOutlined />} loading={uploadingGallery}>
              Add photo
            </Button>
          </Upload>
          <Input
            placeholder="YouTube or Vimeo URL"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            onPressEnter={addVideo}
            style={{ width: 260 }}
          />
          <Button icon={<YoutubeOutlined />} onClick={addVideo}>
            Add video
          </Button>
        </Space>

        {media.length === 0 ? (
          <Text type="secondary">No gallery items yet.</Text>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {media.map((item, index) => {
              const thumb =
                item.type === "image" ? getBlogMediaPublicUrl(item.path) : null;
              return (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "8px 12px",
                    border: "1px solid #f0f0f0",
                    borderRadius: 6,
                  }}
                >
                  {item.type === "image" && thumb ? (
                    <Image
                      src={thumb}
                      alt={item.alt ?? ""}
                      width={56}
                      height={56}
                      style={{ objectFit: "cover", borderRadius: 4 }}
                    />
                  ) : (
                    <YoutubeOutlined style={{ fontSize: 28, color: "#cf1322" }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text ellipsis style={{ display: "block" }}>
                      {item.type === "image"
                        ? item.path.split("/").pop()
                        : item.url}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.type === "image" ? "Photo" : "Video"}
                    </Text>
                  </div>
                  <Space>
                    <Button
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={index === 0}
                      onClick={() => moveItem(index, -1)}
                    />
                    <Button
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={index === media.length - 1}
                      onClick={() => moveItem(index, 1)}
                    />
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => void removeItem(index)}
                    />
                  </Space>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
